// src/components/tabs/DocumentsTab.jsx
// Central document library for the contract — replaces AttachmentsTab
// All files uploaded anywhere on this contract are catalogued here

import { useRef, useState, useEffect } from 'react';
import { EmptyState, Icons } from '../UI';
import { fmtSize, fmtDate, uid, today } from '../../utils/constants';
import supabase from '../../utils/supabaseClient';

// Loads pdf.js from CDN on first use — same pattern as DraftTab.jsx, so a
// corrupted/invalid PDF can be detected before it's stored, rather than
// silently accepted and later sent to Anthropic as a raw document block
// (which causes the whole request to fail).
async function loadPdfJs() {
  if (window.pdfjsLib) return window.pdfjsLib;
  await new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
  window.pdfjsLib.GlobalWorkerOptions.workerSrc =
    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  return window.pdfjsLib;
}
async function getPdfPageCount(base64) {
  try {
    const lib = await loadPdfJs();
    const pdfDoc = await lib.getDocument({ data: atob(base64) }).promise;
    return pdfDoc.numPages;
  } catch (e) { return null; }
}

// Extract plain text from a .docx file using JSZip (already loaded globally
// via a script tag in index.html — same pattern used for other docx uploads
// elsewhere in the app). Lets Word documents be sent to Penny as real text
// instead of mislabeled as application/pdf, which the API rejects outright.
async function extractDocxText(file) {
  const JSZip = window.JSZip;
  if (!JSZip) throw new Error('JSZip not loaded — refresh the page and try again.');
  const buf = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(buf);
  const docXml = zip.file('word/document.xml');
  if (!docXml) throw new Error('Invalid .docx file (no word/document.xml).');
  const xmlStr = await docXml.async('string');
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlStr, 'application/xml');
  const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
  const pNodes = xmlDoc.getElementsByTagNameNS(W_NS, 'p');
  const lines = [];
  for (let i = 0; i < pNodes.length; i++) {
    const tNodes = pNodes[i].getElementsByTagNameNS(W_NS, 't');
    let text = '';
    for (let j = 0; j < tNodes.length; j++) text += tNodes[j].textContent || '';
    lines.push(text.trim());
  }
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

const SOURCE_META = {
  penny_draft:     { label: 'Penny Draft',     color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: '✒' },
  user_uploaded:   { label: 'User Uploaded',   color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', icon: '↑' },
  legal_revised:   { label: 'Legal Revised',   color: '#9333ea', bg: '#fdf4ff', border: '#e9d5ff', icon: '⚖' },
  exec_approved:   { label: 'Exec Approved',   color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: '✓' },
  request_doc:     { label: 'Request Doc',     color: '#0891b2', bg: '#f0f9ff', border: '#bae6fd', icon: '📋' },
  reference:       { label: 'Reference',       color: '#64748b', bg: '#f8fafc', border: '#e2e8f0', icon: '📖' },
  exhibit_final:   { label: 'Exhibit Final',   color: '#c49922', bg: '#fffbeb', border: '#fde68a', icon: '⊞' },
  attachment:      { label: 'Attachment',      color: '#374151', bg: '#f8fafc', border: '#e2e8f0', icon: '📎' },
};

function SourceBadge({ source }) {
  const meta = SOURCE_META[source] || SOURCE_META.user_uploaded;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
      background: meta.bg, color: meta.color, border: '1px solid ' + meta.border,
      display: 'inline-flex', alignItems: 'center', gap: 4,
    }}>
      {meta.icon} {meta.label}
    </span>
  );
}

export default function DocumentsTab({ contract, updateC, toast }) {
  const fileRef = useRef(null);
  const [downloading, setDownloading] = useState({});
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('all');

  // ── Collect ALL documents from all sources ────────────────────────
  function getAllDocuments() {
    const docs = [];

    // Request document (from contract creation)
    const reqDoc = (contract.attachments || []).find(a => a.tag === 'Request Document');
    if (reqDoc) {
      docs.push({
        id: reqDoc.id, name: reqDoc.name || reqDoc.originalName,
        source: 'request_doc', size: reqDoc.size,
        date: reqDoc.date, location: 'Request Document',
        base64: reqDoc.base64,
      });
    }

    // Legacy attachments (non-request-doc)
    (contract.attachments || []).filter(a => a.tag !== 'Request Document').forEach(att => {
      docs.push({
        id: att.id, name: att.name || att.originalName,
        source: 'user_uploaded', size: att.size,
        date: att.date, location: 'Attachments',
        number: att.number,
      });
    });

    // Reference docs
    (contract.referenceDocs || []).forEach(doc => {
      docs.push({
        id: doc.id, name: doc.name,
        source: 'reference', size: doc.size,
        date: doc.addedAt, location: 'Reference Library',
        base64: doc.base64,
      });
    });

    // Exhibit final versions
    (contract.exhibits || []).filter(e => e.finalVersion?.source).forEach(ex => {
      docs.push({
        id: 'ex_' + ex.id, name: 'Exhibit ' + ex.label + ' — ' + (ex.name || 'Unnamed'),
        source: 'exhibit_final', size: ex.finalVersion.size,
        date: ex.finalVersion.uploadedAt, location: 'Assembly — Exhibit ' + ex.label,
        finalSource: ex.finalVersion.source,
        base64: ex.finalVersion.base64,
      });
    });

    // Exhibit-specific attachments
    (contract.exhibits || []).forEach(ex => {
      (ex.attachments || []).forEach((att, i) => {
        if (att.name || att.base64) {
          docs.push({
            id: att.id, name: att.name || ('Attachment ' + (i + 1)),
            source: 'attachment', size: att.size,
            date: att.uploadedAt, location: 'Exhibit ' + ex.label + ' — Attachment ' + (i + 1),
            base64: att.base64,
          });
        }
      });
    });

    // General attachments
    (contract.general_attachments || []).forEach((att, i) => {
      if (att.name || att.base64) {
        docs.push({
          id: att.id, name: att.name || ('General Attachment ' + (i + 1)),
          source: att.source || 'user_uploaded', size: att.size,
          date: att.uploadedAt, location: 'General Attachments',
          base64: att.base64,
        });
      }
    });

    // Penny drafts (from pennyDrafts)
    const pennyDrafts = contract.pennyDrafts || {};
    if (pennyDrafts.sow) {
      docs.push({
        id: 'penny_sow', name: 'Exhibit A — SOW (Penny Draft)',
        source: 'penny_draft', size: null,
        date: null, location: 'Penny Tab — Exhibit A',
        isText: true, content: pennyDrafts.sow,
      });
    }
    if (pennyDrafts.results?.sol) {
      docs.push({
        id: 'penny_sol', name: 'Solicitation Document (Penny Draft)',
        source: 'penny_draft', size: null,
        date: null, location: 'Penny Tab — Solicitation',
        isText: true, content: pennyDrafts.results.sol,
      });
    }

    return docs;
  }

  // ── Upload new document directly to Documents tab ─────────────────
  async function uploadDocument(file) {
    const isDocx = /\.docx$/i.test(file.name);

    if (isDocx) {
      try {
        const extractedText = await extractDocxText(file);
        const id = uid();
        const newDoc = {
          id, name: file.name.replace(/\.docx$/i, ''),
          source: 'user_uploaded', size: file.size,
          uploadedAt: new Date().toISOString(),
          isText: true, content: extractedText,
        };
        updateC({ general_attachments: [...(contract.general_attachments || []), newDoc] });
        toast.success(file.name + ' added to Documents (text extracted)');
      } catch (e) {
        toast.error('Could not read this Word document: ' + e.message);
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1];
      const id = uid();

      // Validate the PDF is actually readable before storing it. This file
      // gets sent to Anthropic as a raw document block from several Penny
      // functions with no further checks — an invalid/corrupted file stored
      // here would get shipped to the AI on every one of those calls,
      // causing each to fail outright.
      const pages = await getPdfPageCount(base64);
      if (!pages) {
        toast.error('"' + file.name + '" doesn\'t look like a valid, readable PDF — please check the file and try again.');
        return;
      }

      // Save to Supabase Storage
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const bytes = atob(base64);
          const arr = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
          const blob = new Blob([arr], { type: 'application/pdf' });
          await supabase.storage.from('attachment-files')
            .upload(user.id + '/' + contract.id + '/' + id, blob, { upsert: true });
        }
      } catch (e) { console.error('Upload error:', e); }

      // Add to general_attachments
      const newDoc = {
        id, name: file.name.replace(/\.pdf$/i, ''),
        source: 'user_uploaded', size: file.size,
        uploadedAt: new Date().toISOString(),
        base64,
      };
      updateC({ general_attachments: [...(contract.general_attachments || []), newDoc] });
      toast.success(file.name + ' added to Documents');
    };
    reader.readAsDataURL(file);
  }

  // ── Download document ─────────────────────────────────────────────
  async function downloadDoc(doc) {
    if (doc.isText) {
      // Text content — download as .txt
      const blob = new Blob([doc.content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = doc.name + '.txt';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      return;
    }
    if (doc.base64) {
      const bytes = atob(doc.base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = doc.name + '.pdf';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      return;
    }
    // Try Supabase Storage
    setDownloading(p => ({ ...p, [doc.id]: true }));
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase.storage
          .from('attachment-files')
          .download(user.id + '/' + contract.id + '/' + doc.id);
        if (data) {
          const url = URL.createObjectURL(data);
          const a = document.createElement('a');
          a.href = url; a.download = doc.name + '.pdf';
          document.body.appendChild(a); a.click();
          document.body.removeChild(a); URL.revokeObjectURL(url);
        } else {
          toast.warn('File not found in storage');
        }
      }
    } catch (e) { toast.error('Download failed: ' + e.message); }
    finally { setDownloading(p => ({ ...p, [doc.id]: false })); }
  }

  const allDocs = getAllDocuments();

  // Filter
  const filtered = allDocs.filter(doc => {
    const matchesSearch = !search || doc.name.toLowerCase().includes(search.toLowerCase()) || doc.location.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filterSource === 'all' || doc.source === filterSource;
    return matchesSearch && matchesFilter;
  });

  // Group by source
  const sources = [...new Set(allDocs.map(d => d.source))];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy-900)', margin: 0 }}>Documents</h2>
          <p style={{ fontSize: 13, color: 'var(--slate-500)', marginTop: 4, margin: 0 }}>
            All files associated with this contract — {allDocs.length} document{allDocs.length !== 1 ? 's' : ''} from {sources.length} source{sources.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-gold btn-sm" onClick={() => fileRef.current?.click()}>
            <Icons.upload /> Upload Document
          </button>
          <input ref={fileRef} type="file" accept=".pdf,.docx" style={{ display: 'none' }}
            onChange={e => { if (e.target.files[0]) uploadDocument(e.target.files[0]); e.target.value = ''; }} />
        </div>
      </div>

      {allDocs.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="📁"
            title="No documents yet"
            subtitle="Documents uploaded anywhere on this contract — in the Penny tab, Assembly tab, or here — will all appear in this library."
            action={
              <button className="btn btn-gold" onClick={() => fileRef.current?.click()}>
                <Icons.upload /> Upload First Document
              </button>
            }
          />
        </div>
      ) : (
        <>
          {/* Search + filter */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search documents…"
                style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1.5px solid var(--slate-200)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }}
              />
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--slate-400)', fontSize: 14 }}>🔍</span>
            </div>
            <select
              value={filterSource}
              onChange={e => setFilterSource(e.target.value)}
              style={{ padding: '8px 12px', border: '1.5px solid var(--slate-200)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: '#fff' }}>
              <option value="all">All sources</option>
              {sources.map(s => (
                <option key={s} value={s}>{SOURCE_META[s]?.label || s}</option>
              ))}
            </select>
          </div>

          {/* Document list */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--slate-400)', fontSize: 13 }}>
                No documents match your search
              </div>
            ) : (
              filtered.map((doc, i) => (
                <div key={doc.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 16px',
                  borderBottom: i < filtered.length - 1 ? '1px solid var(--slate-100)' : 'none',
                  transition: 'background .1s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--slate-50)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                  {/* File icon */}
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                    background: SOURCE_META[doc.source]?.bg || '#f8fafc',
                    border: '1px solid ' + (SOURCE_META[doc.source]?.border || '#e2e8f0'),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16,
                  }}>
                    {doc.isText ? '📝' : '📄'}
                  </div>

                  {/* Doc info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {doc.name}
                      </div>
                      <SourceBadge source={doc.source} />
                    </div>
                    <div style={{ display: 'flex', gap: 10, fontSize: 11, color: 'var(--slate-400)' }}>
                      <span>📍 {doc.location}</span>
                      {doc.size && <span>{fmtSize(doc.size)}</span>}
                      {doc.date && <span>{fmtDate(doc.date?.split('T')[0] || doc.date)}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      onClick={() => downloadDoc(doc)}
                      disabled={downloading[doc.id]}
                      className="btn btn-ghost btn-sm">
                      {downloading[doc.id] ? '…' : <><Icons.download /> Download</>}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--slate-400)', textAlign: 'center' }}>
            {filtered.length} of {allDocs.length} documents shown · Files are stored securely in GoldenScope
          </div>
        </>
      )}
    </div>
  );
}
